import axiosInstance from "../utils/api";

export const generateEmail = async (
  data,
  onDataChunk,
  onComplete,
  onError
) => {
  let accumulatedData = [];
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${axiosInstance.defaults.baseURL}/generate/email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
        Accept: "text/event-stream",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorMsg = `HTTP error! status: ${response.status} - ${response.statusText}`;
      if (onError) onError(new Error(errorMsg));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let errorOccurred = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (!errorOccurred && onComplete) await onComplete();
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep the last (potentially partial) line

      for (const line of lines) {
        if (line.trim().startsWith("data:")) {
          // This handles "data: {...}" and "data: data: {...}" by finding the first brace
          const jsonStartIndex = line.indexOf('{');
          if (jsonStartIndex > -1) {
            const jsonString = line.substring(jsonStartIndex);
            try {
              const parsed = JSON.parse(jsonString);
              accumulatedData.push(parsed);
              if (onDataChunk) {
                onDataChunk(parsed);
              }
            } catch (err) {
              console.warn('Failed to parse JSON chunk:', err.message);
              
              // Try to fix malformed JSON before giving up
              try {
                const fixedJson = fixMalformedJson(jsonString);
                const parsed = JSON.parse(fixedJson);
                accumulatedData.push(parsed);
                if (onDataChunk) {
                  onDataChunk(parsed);
                }
              } catch (fixErr) {
                console.warn('Failed to fix malformed JSON:', fixErr.message);
                
                // Try to extract content directly from the malformed data
                const directContent = extractHtmlContentDirectly(jsonString);
                if (directContent) {
                  const fallbackData = { content: directContent, _malformed: true };
                  accumulatedData.push(fallbackData);
                  if (onDataChunk) {
                    onDataChunk(fallbackData);
                  }
                } else {
                  // Try to handle unquoted JSON response
                  const unquotedContent = handleUnquotedJsonResponse(jsonString);
                  if (unquotedContent) {
                    const fallbackData = { content: unquotedContent, _malformed: true };
                    accumulatedData.push(fallbackData);
                    if (onDataChunk) {
                      onDataChunk(fallbackData);
                    }
                  } else {
                    // If all else fails, log the error but continue processing
                    console.warn('Could not parse or fix malformed JSON chunk:', jsonString.substring(0, 100));
                  }
                }
              }
            }
          }
        }
      }
      if (errorOccurred) break;
    }
    return accumulatedData;
  } catch (error) {
    console.error("Streaming error:", error);
    if (onError) onError(new Error(error.message || "Streaming error occurred. Please try again."));
    return accumulatedData;
  }
};

export const getEmailTemplate = async (templateId) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await axiosInstance.get(`/api/v1/email_templates/${templateId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (!response.data) {
      throw new Error("No data received from server");
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching email template:", error);
    if (error.response) {
      throw new Error(error.response.data.message || `Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw new Error(error.message || "Error fetching email template");
    }
  }
};

export const fetchEmailTemplates = async (params = {}) => {
  const {
    sort_type = 'newest',
    filter_column = 'name',
    filter_value = '',
    page = 0,
    per_page = 10
  } = params;

  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Unauthorized: No token found");

    const response = await axiosInstance.get(`/api/v1/email_templates`, {
      params: {
        sort_type,
        filter_column,
        filter_value,
        page,
        per_page
      },
      headers: {
        Authorization: token
      }
    });

    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error("Error fetching email templates:", error);
    throw error;
  }
};

export const deleteEmailTemplate = async (id) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  try {
    const response = await axiosInstance.delete(`/api/v1/email_templates/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting email template:", error);
    throw error;
  }
};

export const updateEmailTemplateStatus = async (id, status) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  try {
    const formData = new FormData();
    formData.append("email_template[status]", status);
    const response = await axiosInstance.patch(`/api/v1/email_templates/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error updating email template status:", error);
    throw error;
  }
};

export const updateOrCreateEmailTemplate = async (id, data) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  
  try {
    const formData = new FormData();
    
    // Add foray_response as a stringified JSON
    if (data.foray_response) {
      formData.append("email_template[foray_response]", JSON.stringify(data.foray_response));
    }
    
    // Add other fields with exact names from curl
    if (data.name) {
      formData.append("email_template[name]", data.name);
    }
    if (data.tone) {
      formData.append("email_template[tone]", data.tone);
    }
    if (data.preview_url) {
      formData.append("email_template[preview_url]", data.preview_url);
    }
    if (data.occasion) {
      formData.append("email_template[occasion]", data.occasion);
    }
    if (data.further_details) {
      formData.append("email_template[further_details]", data.further_details);
    }
    if (data.status) {
      formData.append("email_template[status]", data.status || 'draft');
    }
    
    // Handle images array with exact format from curl
    if (data.images && Array.isArray(data.images)) {
      data.images.forEach(image => {
        formData.append("email_template[images][]", image);
      });
    }
    
    // Handle image_names array with exact format from curl
    if (data.image_names && Array.isArray(data.image_names)) {
      data.image_names.forEach(name => {
        formData.append("email_template[image_names][]", name);
      });
    }

    // Use POST for new templates, PATCH for updates
    const response = await axiosInstance[id ? 'patch' : 'post'](
      `/api/v1/email_templates${id ? `/${id}` : ''}`, 
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error updating email template:", error);
    throw error;
  }
};

// Helper function to fix common malformed JSON issues
function fixMalformedJson(jsonString) {
  let fixed = jsonString;
  
  // First, check if this is already valid JSON
  try {
    JSON.parse(fixed);
    return fixed; // Already valid, return as-is
  } catch (e) {
    // Not valid JSON, proceed with fixing
  }
  
  // Handle completely unquoted JSON (like the failing response)
  // Pattern: key: value instead of "key": "value"
  
  // Fix missing quotes around keys (more comprehensive)
  fixed = fixed.replace(/(\w+(?:_\w+)*):/g, '"$1":');
  
  // Fix missing quotes around string values, but preserve numbers, booleans, null, and objects
  // This regex is more sophisticated to avoid over-quoting
  fixed = fixed.replace(/:\s*([^",\{\}\[\],\s][^,\{\}\[\]]*[^",\{\}\[\],\s])\s*([,}])/g, (match, value, endChar) => {
    // Don't quote numbers, booleans, null, or objects
    if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
      return `: ${value}${endChar}`;
    }
    // Quote string values
    return `: "${value}"${endChar}`;
  });
  
  // Fix missing quotes around simple string values at the end of objects
  fixed = fixed.replace(/:\s*([^",\{\}\[\],\s]+)\s*([,}])/g, (match, value, endChar) => {
    // Don't quote numbers, booleans, null, or objects
    if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
      return `: ${value}${endChar}`;
    }
    // Quote string values
    return `: "${value}"${endChar}`;
  });
  
  // Fix missing quotes around values that end with quotes
  fixed = fixed.replace(/:\s*([^",\{\}\[\],\s][^"]*)"\s*([,}])/g, ': "$1"$2');
  
  // Fix missing quotes around values that start with quotes
  fixed = fixed.replace(/:\s*"\s*([^"]+)\s*([,}])/g, ': "$1"$2');
  
  // Handle edge case where the last value might not have a comma
  fixed = fixed.replace(/:\s*([^",\{\}\[\],\s][^,\{\}\[\]]*[^",\{\}\[\],\s])\s*}$/g, (match, value) => {
    if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
      return `: ${value}}`;
    }
    return `: "${value}"}`;
  });
  
  return fixed;
}

// Helper function to extract HTML content directly from malformed data
function extractHtmlContentDirectly(data) {
  try {
    console.log('Attempting direct HTML extraction from data:', data.substring(0, 300) + '...');
    
    // Look for html_content field in the data (multiple patterns for malformed JSON)
    let htmlMatch = data.match(/"html_content"\s*:\s*"([^"]+)"/);
    if (!htmlMatch) {
      // Try without quotes around the key (malformed JSON)
      htmlMatch = data.match(/html_content\s*:\s*"([^"]+)"/);
    }
    if (!htmlMatch) {
      // Try with fragmented key
      htmlMatch = data.match(/html\s*_content\s*:\s*"([^"]+)"/);
    }
    if (!htmlMatch) {
      // Try to find HTML content after "html_content" even if structure is broken
      const htmlContentIndex = data.indexOf('html_content');
      if (htmlContentIndex !== -1) {
        const afterHtmlContent = data.substring(htmlContentIndex);
        const htmlValueMatch = afterHtmlContent.match(/:\s*"([^"]*<[^"]*>[^"]*)/);
        if (htmlValueMatch) {
          const content = htmlValueMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
          console.log('Found fragmented html_content field:', content.substring(0, 100) + '...');
          return content;
        }
      }
    }
    
    // Handle completely unquoted malformed JSON (like the failing response)
    if (!htmlMatch) {
      // Look for pattern: html_content: "content" (unquoted key, quoted value)
      htmlMatch = data.match(/html_content\s*:\s*"([^"]+)"/);
    }
    if (!htmlMatch) {
      // Look for pattern: html_content: content (unquoted key and value)
      const htmlContentIndex = data.indexOf('html_content');
      if (htmlContentIndex !== -1) {
        const afterHtmlContent = data.substring(htmlContentIndex);
        // Look for content after the colon, handling both quoted and unquoted values
        const htmlValueMatch = afterHtmlContent.match(/:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
        if (htmlValueMatch) {
          const content = htmlValueMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
          console.log('Found unquoted html_content field:', content.substring(0, 100) + '...');
          return content;
        }
      }
    }
    
    if (htmlMatch) {
      const content = htmlMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      console.log('Found html_content field:', content.substring(0, 100) + '...');
      return content;
    }
    
    // Look for content field (also handle unquoted keys)
    let contentMatch = data.match(/"content"\s*:\s*"([^"]+)"/);
    if (!contentMatch) {
      // Try without quotes around the key (malformed JSON)
      contentMatch = data.match(/content\s*:\s*"([^"]+)"/);
    }
    if (!contentMatch) {
      // Try unquoted key and value
      const contentIndex = data.indexOf('content');
      if (contentIndex !== -1) {
        const afterContent = data.substring(contentIndex);
        const contentValueMatch = afterContent.match(/:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
        if (contentValueMatch) {
          const content = contentValueMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
          console.log('Found unquoted content field:', content.substring(0, 100) + '...');
          return content;
        }
      }
    }
    
    if (contentMatch) {
      const content = contentMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      console.log('Found content field:', content.substring(0, 100) + '...');
      return content;
    }
    
    // Look for HTML-like content between quotes that contains HTML tags
    const htmlLikeMatch = data.match(/"[^"]*<[^"]*>[^"]*"/);
    if (htmlLikeMatch) {
      const content = htmlLikeMatch[0].replace(/^"/, '').replace(/"$/, '');
      console.log('Found HTML-like content:', content.substring(0, 100) + '...');
      return content;
    }
    
    // Look for any content that looks like HTML (broader search)
    const broadHtmlMatch = data.match(/<[^>]+>[^<]*<\/[^>]+>/);
    if (broadHtmlMatch) {
      console.log('Found broad HTML match:', broadHtmlMatch[0].substring(0, 100) + '...');
      return broadHtmlMatch[0];
    }
    
    // Try to extract content from the malformed JSON structure
    // Look for any text that appears to be HTML content in the response
    const potentialHtmlMatch = data.match(/(?:html_content|content)\s*:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
    if (potentialHtmlMatch) {
      const content = potentialHtmlMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      console.log('Found potential HTML content in malformed JSON:', content.substring(0, 100) + '...');
      return content;
    }
    
    console.log('No HTML content found through direct extraction');
    return null;
  } catch (error) {
    console.warn('Error extracting HTML content directly:', error);
    return null;
  }
}

// Helper function to handle completely unquoted JSON responses
function handleUnquotedJsonResponse(data) {
  try {
    console.log('Attempting to handle unquoted JSON response');
    
    // Look for patterns like: content_type: ayurvedic_response, metadata: { query_type: article }
    // This handles the failing response format
    
    // First, try to find any HTML content in the response
    const htmlContentMatch = data.match(/(?:html_content|content)\s*:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
    if (htmlContentMatch) {
      const content = htmlContentMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      console.log('Found HTML content in unquoted response:', content.substring(0, 100) + '...');
      return content;
    }
    
    // If no HTML content found, try to reconstruct a valid JSON structure
    // Convert: content_type: ayurvedic_response, metadata: { query_type: article }
    // To: { "content_type": "ayurvedic_response", "metadata": { "query_type": "article" } }
    
    let reconstructedJson = data;
    
    // Add quotes around keys
    reconstructedJson = reconstructedJson.replace(/(\w+(?:_\w+)*):/g, '"$1":');
    
    // Add quotes around string values (but not around numbers, booleans, null, or objects)
    reconstructedJson = reconstructedJson.replace(/:\s*([^",\{\}\[\],\s][^,\{\}\[\]]*[^",\{\}\[\],\s])\s*([,}])/g, (match, value, endChar) => {
      // Don't quote numbers, booleans, null, or objects
      if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
        return `: ${value}${endChar}`;
      }
      // Quote string values
      return `: "${value}"${endChar}`;
    });
    
    // Handle the last value in the object
    reconstructedJson = reconstructedJson.replace(/:\s*([^",\{\}\[\],\s][^,\{\}\[\]]*[^",\{\}\[\],\s])\s*}$/g, (match, value) => {
      if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
        return `: ${value}}`;
      }
      return `: "${value}"}`;
    });
    
    // Wrap in braces if not already wrapped
    if (!reconstructedJson.trim().startsWith('{')) {
      reconstructedJson = '{' + reconstructedJson + '}';
    }
    
    console.log('Reconstructed JSON:', reconstructedJson.substring(0, 200) + '...');
    
    // Try to parse the reconstructed JSON
    try {
      const parsed = JSON.parse(reconstructedJson);
      
      // Extract HTML content from the parsed data
      if (parsed.html_content) {
        return parsed.html_content;
      } else if (parsed.content) {
        return parsed.content;
      }
      
      // If no HTML content found, return the reconstructed JSON as a fallback
      return reconstructedJson;
    } catch (parseError) {
      console.warn('Failed to parse reconstructed JSON:', parseError);
      // Return the reconstructed JSON as a fallback
      return reconstructedJson;
    }
  } catch (error) {
    console.warn('Error handling unquoted JSON response:', error);
    return null;
  }
}